import type { ArcualCurrency } from '@/components/shared/constants';
import { i18n } from '@/i18n';
import type { AudienceFilters } from '@/model/activation/audience/AudienceFilters';
import type { ViewingRoomFilters, ViewingRoomsPayload } from '@/model/activation/viewing-rooms/ViewingRoomFilters';
import { isActivationViewingRoom } from '@/utils/activation/activationHelpers';
import { handleHttpError } from '@/utils/errorHandlers';
import { enrichInventoryItem } from '@/utils/inventoryUtils';
import { listingDetailsMessages } from '@/views/activation/configure-activation/listings/listing-detail/listing-details.messages';
import { NeonAlertService } from '@arcual/lib-app-neon';
import {
  type Activation,
  type ActivationListingFilters,
  type ActivationRequest,
  type ActivationResponse,
  type ActivationService,
  type ArcualInventoryItem,
  type ArcualListingStatus,
  type AudienceResponse,
  type ContentResponse,
  getActivationService,
  type GetExhibitorsParams,
  getInventoryService,
  type HttpError,
  type InventoryItemBulkActions,
  type LegalTermsResponse,
  type PublicActivation,
  type ResultList,
  type StyleResponse,
  type UpdateActivationListingsPayload,
  type UpdatedLegalTerms,
} from '@arcual/lib-app-platform';
import { defineStore } from 'pinia';
import { computed, type Ref, ref } from 'vue';
import { useRoute } from 'vue-router';

export const ActivationStoreName = 'ActivationStore';

export const useActivationStore = defineStore(
  ActivationStoreName,
  () => {
    const activation = ref<ActivationResponse | null>(null);
    const legalTerms = ref<LegalTermsResponse | null>(null);
    const route = useRoute();
    const listings: Ref<Map<string, Ref<ArcualInventoryItem>>> = ref<Map<string, Ref<ArcualInventoryItem>>>(new Map());
    const styles: Ref<Map<string, Ref<StyleResponse | undefined>>> = ref<Map<string, Ref<StyleResponse | undefined>>>(
      new Map(),
    );

    const _setListing = async (item: ArcualInventoryItem) => {
      await enrichInventoryItem(item);
      const itemRef = listings.value.get(item.id);
      if (itemRef) {
        itemRef.value = {
          ...itemRef.value,
          ...item,
        };
      } else {
        listings.value.set(item.id, ref(item));
      }
    };

    const hiddenActivations = ref<string[]>([]);
    const createActivation = async (payload: Activation): Promise<ActivationResponse> => {
      try {
        const response = await getActivationService().createActivation(payload);
        activation.value = response;
        return response;
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const isDashboardInfoHidden = computed(() => {
      const activationId = activation.value?.id;
      return activationId ? hiddenActivations.value.includes(activationId) : false;
    });

    const hideDashboardInfo = () => {
      const activationId = activation.value?.id;
      if (activationId && !hiddenActivations.value.includes(activationId)) {
        hiddenActivations.value.push(activationId);
      }
    };

    const getUserLegalTerms = async (payload: ActivationRequest): Promise<LegalTermsResponse | null> => {
      try {
        legalTerms.value = await getActivationService().getUserLegalTerms(payload);
        return legalTerms.value;
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateUserLegalTerms = async (payload: UpdatedLegalTerms): Promise<LegalTermsResponse> => {
      try {
        return await getActivationService().updateUserLegalTerms(payload);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivationContent = async (payload: ActivationRequest): Promise<ContentResponse> => {
      try {
        return await getActivationService().getActivationContent(payload);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateActivationContent = async (payload: ContentResponse): Promise<ContentResponse> => {
      try {
        return await getActivationService().updateActivationContent(payload);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivationStyle = (activationId: string): Ref<StyleResponse | undefined> => {
      const cachedStyle = styles.value.get(activationId);

      if (!cachedStyle) {
        const style: Ref<StyleResponse | undefined> = ref(undefined);
        styles.value.set(activationId, style);

        getActivationService()
          .getActivationStyle({ activationId })
          .then((response: StyleResponse) => {
            style.value = response;
          })
          .catch((error: HttpError) => {
            handleHttpError(error);
          });
      }

      return styles.value.get(activationId) ?? ref(undefined);
    };

    const updateActivationStyle = async (style: StyleResponse, formData: FormData): Promise<void> => {
      const updatedStyle = await getActivationService().updateActivationStyle(style, formData);
      styles.value.set(style.activationId, ref(updatedStyle));
    };

    const getActivations = async (): Promise<ActivationResponse[]> => {
      try {
        return await getActivationService().getActivations();
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivation = async (id = route.params.id as string): Promise<ActivationResponse | null> => {
      try {
        activation.value = await getActivationService().getActivation(id);
        return activation.value;
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getOperatorIdFromParentActivation = async (id: string): Promise<string> => {
      try {
        const { operatorId } = await getActivationService().getActivation(id);
        return operatorId;
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getViewingRoom = async (viewingRoomId: string): Promise<Activation> => {
      try {
        return await getActivationService().getActivation(viewingRoomId);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getPublicActivation = async (activationId: string): Promise<PublicActivation> => {
      return getActivationService()
        .getPublicActivation(activationId)
        .catch((error: HttpError) => {
          return Promise.reject(error);
        });
    };

    const getPublicActivationExhibitors = async (activationId: string, params: GetExhibitorsParams) => {
      try {
        return await getActivationService().getPublicActivationExhibitors(activationId, params);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateActivation = async (
      payload: Activation,
      id = route.params.id as string,
    ): Promise<ActivationResponse> => {
      try {
        return await getActivationService().updateActivation(id, payload);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const publishActivation = async (activationId: string): Promise<void> => {
      try {
        await getActivationService().publishActivation(activationId);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getListedItems = async (
      activationId: string,
      filters?: ActivationListingFilters,
    ): Promise<ResultList<ArcualInventoryItem>> => {
      try {
        const resultList = await getActivationService().getListedItems(activationId, filters);
        const ids = resultList.items.map((item: ArcualInventoryItem) => item.id);
        // inject actions
        const actionsList = await getInventoryService().getBulkActions(ids);

        for (const item of resultList.items) {
          item.actions = actionsList.find((action: InventoryItemBulkActions) => action.id === item.id);
          await _setListing(item);
        }

        return resultList;
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getListedItem = async (
      activationId: string,
      itemId: string,
    ): Promise<Ref<ArcualInventoryItem | undefined>> => {
      const item = await getActivationService().getListedItem(activationId, itemId);
      await _setListing(item);
      return listings.value.get(itemId) ?? ref(undefined);
    };

    const updateListing = async (
      listing: ArcualInventoryItem,
      silent = false,
    ): Promise<Ref<ArcualInventoryItem | undefined>> => {
      try {
        if (activation.value && activation.value.id) {
          await getActivationService().updateListing(activation.value?.id, listing);
          // TODO: rather than update this directly we should use the updateListing service response when the BE team
          // adjusts to the new data model
          await _setListing(listing);
          if (!silent) {
            NeonAlertService.success({
              title: listingDetailsMessages.listingUpdated,
              message: i18n.global.t('platform.app.activations.listings.details.updatedNotificationDescription', {
                price: listing.priceVisibility?.toString() || '',
              }),
            });
          }
          return getListedItem(activation.value.id, listing.id);
        } else {
          handleHttpError('Error: No Activation loaded');
          return Promise.reject('Error: No Activation loaded');
        }
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateViewingRoomListing = async (
      activation: ActivationResponse,
      listing: ArcualInventoryItem,
      silent = false,
    ): Promise<Ref<ArcualInventoryItem | undefined>> => {
      try {
        if (isActivationViewingRoom(activation)) {
          await getActivationService().updateViewingRoomListing(
            activation.childMetadata!.parentActivationId,
            activation.id,
            listing,
          );
          // TODO: rather than update this directly we should use the updateListing service response when the BE team
          // adjusts to the new data model
          await _setListing(listing);
          if (!silent) {
            NeonAlertService.success({
              title: listingDetailsMessages.listingUpdated,
              message: i18n.global.t('platform.app.activations.listings.details.updatedNotificationDescription', {
                price: listing.priceVisibility?.toString() || '',
              }),
            });
          }
          return getListedItem(activation.id, listing.id);
        } else {
          handleHttpError('Error: No Activation loaded');
          return Promise.reject('Error: No Activation loaded');
        }
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateListingStatus = async (listing: ArcualInventoryItem, status: ArcualListingStatus): Promise<void> => {
      if (activation.value && activation.value.id) {
        return getActivationService().updateListingStatus(activation.value.id, listing.id, status);
      }

      return undefined;
    };

    const updateActivationListings = async (
      activationId: string,
      payload: UpdateActivationListingsPayload,
    ): Promise<void> => {
      try {
        await getActivationService().updateActivationListings(activationId, payload);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateViewingRoomListingsFromParentList = async (
      activation: ActivationResponse,
      payload: UpdateActivationListingsPayload,
    ): Promise<void> => {
      try {
        if (isActivationViewingRoom(activation)) {
          await (getActivationService() as ActivationService).updateViewingRoomListingsFromParentList(
            activation.childMetadata!.parentActivationId,
            activation.id,
            payload,
          );
        } else {
          handleHttpError('Error: No Activation loaded');
          return Promise.reject('Error: No Activation loaded');
        }
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivationExhibitors = async (activationId: string, params: GetExhibitorsParams) => {
      try {
        return await getActivationService().getActivationExhibitors(activationId, params);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivationExhibitor = async (activationId: string, exhibitorId: string) => {
      try {
        return await getActivationService().getActivationExhibitor(activationId, exhibitorId);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getExhibitorInformation = async (
      activationId: string,
    ): Promise<{
      activationId: string;
      exhibitorInformationSection: string;
    }> => {
      try {
        return await getActivationService().getExhibitorInformation(activationId);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const updateExhibitorInformation = async (
      activationId: string,
      exhibitorInformationSection: string,
    ): Promise<{
      activationId: string;
      exhibitorInformationSection: string;
    }> => {
      try {
        return await getActivationService().updateExhibitorInformation(activationId, exhibitorInformationSection);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getActivationViewingRooms = async (
      activationId: string,
      params: ViewingRoomFilters,
    ): Promise<ResultList<ActivationResponse>> => {
      try {
        return await getActivationService().getActivationViewingRooms(activationId, params);
      } catch (error) {
        handleHttpError(error);

        return { total: 0, items: [] } as ResultList<ActivationResponse>;
        // return Promise.reject(error);
      }
    };

    const createPublicActivationViewingRoom = async (
      activation: Activation & { activationCurrency?: ArcualCurrency | null },
      payload: ViewingRoomsPayload,
    ) => {
      try {
        return await getActivationService().createActivationViewingRoom(activation.id!, {
          ...payload,
          isApiOnly: activation.isApiOnly ?? false,
          activationTypeEnum: activation.activationTypeEnum,
          activationCurrency: activation.activationCurrency ?? null,
        });
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const getAudienceForStoreFronts = async (activationId: string, params: AudienceFilters) => {
      try {
        return await getActivationService().getActivationAudience(activationId, params);
      } catch (error) {
        handleHttpError(error);

        return { total: 0, items: [] } as ResultList<AudienceResponse>;
      }
    };

    const getAudienceForViewingRoom = async (activationId: string, viewingRoomId: string, params: AudienceFilters) => {
      try {
        return await getActivationService().getViewingRoomAudience(activationId, viewingRoomId, params);
      } catch (error) {
        handleHttpError(error);

        return { total: 0, items: [] } as ResultList<AudienceResponse>;
      }
    };

    const createAudienceForViewingRoom = async (activationId: string, viewingRoomId: string, audienceIds: string[]) => {
      try {
        return await getActivationService().createAudienceForViewingRoom(activationId, viewingRoomId, audienceIds);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const removeAudienceForViewingRoom = async (activationId: string, viewingRoomId: string, audienceIds: string[]) => {
      try {
        return await getActivationService().removeAudienceForViewingRoom(activationId, viewingRoomId, audienceIds);
      } catch (error) {
        handleHttpError(error);
        return Promise.reject(error);
      }
    };

    const reset = () => {
      activation.value = null;
      legalTerms.value = null;
      listings.value = new Map();
      styles.value = new Map();
      hiddenActivations.value = [];
    };

    return {
      getUserLegalTerms,
      createActivation,
      updateUserLegalTerms,
      getActivations,
      activation,
      legalTerms,
      getActivation,
      getPublicActivation,
      getPublicActivationExhibitors,
      updateActivation,
      publishActivation,
      getListedItems,
      getListedItem,
      updateListing,
      updateListingStatus,
      updateActivationListings,
      getActivationContent,
      getActivationStyle,
      updateActivationStyle,
      updateActivationContent,
      hiddenActivations,
      isDashboardInfoHidden,
      hideDashboardInfo,
      getActivationExhibitors,
      getActivationExhibitor,
      getExhibitorInformation,
      updateExhibitorInformation,
      getActivationViewingRooms,
      getAudienceForStoreFronts,
      reset,
      createPublicActivationViewingRoom,
      getViewingRoom,
      updateViewingRoomListing,
      getAudienceForViewingRoom,
      createAudienceForViewingRoom,
      removeAudienceForViewingRoom,
      getOperatorIdFromParentActivation,
      updateViewingRoomListingsFromParentList,
    };
  },
  {
    persist: {
      paths: ['hiddenActivations'],
    },
  },
);
